import React from 'react';
import { useHistory } from 'react-router-dom';
import { Icon } from 'semantic-ui-react';
import { useCreateFromDocumentProgress } from './CreateFromDocumentProgressProvider';

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 20,
};

const iconColStyle: React.CSSProperties = {
  flexShrink: 0,
  paddingTop: 6,
  width: 10
};

const dotBase: React.CSSProperties = {
  display: 'block',
  width: 10,
  height: 10,
  borderRadius: '50%',
};

const processingIconStyle: React.CSSProperties = {
  position: 'relative',
  display: 'block',
  width: 17,
  height: 17,
  marginTop: -4,
};

const processingCenterDotStyle: React.CSSProperties = {
  ...dotBase,
  position: 'absolute',
  top: 4.5,
  left: 4.5,
  width: 8,
  height: 8,
  backgroundColor: '#f2711c',
};

const processingOrbitStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: 17,
  height: 17,
  borderRadius: '50%',
  border: '2px solid #ffd8b5',
};

const processingArcStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: 17,
  height: 17,
  borderRadius: '50%',
  border: '2px solid rgba(0, 0, 0, 0)',
  borderTopColor: '#f2711c',
  borderRightColor: '#f2711c',
  boxSizing: 'border-box',
  transformOrigin: '50% 50%',
  willChange: 'transform',
  animation: 'cfd-spin 0.8s linear infinite',
};

export default function CreateFromDocumentProgressIndicator() {
  const progress = useCreateFromDocumentProgress();
  const history = useHistory();
  const [showDetails, setShowDetails] = React.useState(false);
  const [copiedContext, setCopiedContext] = React.useState(false);

  const handleCopyContext = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedContext(true);
      setTimeout(() => setCopiedContext(false), 1500);
    } catch {
      // clipboard unavailable; ignore
    }
  };

  const handleViewProject = (projectId: string) => {
    progress?.dismiss();
    history.push(`/project/${projectId}/worksheet`);
  };

  const handleViewPortfolio = (portfolioId: string) => {
    progress?.dismiss();
    history.push(`/portfolio/${portfolioId}`);
  };

  if (!progress || !progress.status) return null;

  const activeStatus = progress.status;
  const activeStatusMessage = progress.statusMessage ?? 'Starting…';

  if (activeStatus === 'uploading' || activeStatus === 'processing') {
    return (
      <>
        <style>{'@keyframes cfd-spin { to { transform: rotate(360deg); } }'}</style>
        <div className="card" style={rowStyle}>
          <div style={iconColStyle}>
            <span style={processingIconStyle}>
              <span style={processingCenterDotStyle} />
              <span style={processingOrbitStyle} />
              <span style={processingArcStyle} />
            </span>
          </div>
          <div>
            <h3 style={{ marginBottom: '4px', fontSize: '18px' }}>Creating your deal</h3>
            <p style={{ marginBottom: '4px' }}>{activeStatusMessage}</p>
            <p style={{ fontSize: '12px', color: '#777' }}>Approx. 1 minute remaining. You can safely leave this page.</p>
          </div>
        </div>
      </>
    );
  }

  if (activeStatus === 'complete' && progress?.result && 'type' in progress.result) {
    const result = progress.result;
    if (result.type === 'project') {
      return (
        <div className="card" style={rowStyle}>
          <div style={iconColStyle}><Icon name="check circle" style={{ color: '#21ba45', margin: 0, marginTop: '-6px', display: 'block' }} /></div>
          <div>
            <h3 style={{ marginBottom: '4px', fontSize: '18px' }}>Project created</h3>
            <p style={{ marginBottom: '4px' }}>
              {result.projectName
                ? <><strong>{result.projectName}</strong> has been created from the document.</>
                : 'Your project has been created from the document.'}
            </p>
            <p>
              <a
                href={`/project/${result.projectId}/worksheet`}
                onClick={(e) => {
                  e.preventDefault();
                  handleViewProject(result.projectId);
                }}
                style={{ color: 'var(--color-primary)', textDecoration: 'underline', fontWeight: 500 }}
              >
                View project &rarr;
              </a>
            </p>
          </div>
        </div>
      );
    }
    if (result.type === 'portfolio') {
      return (
        <div className="card" style={rowStyle}>
          <div style={iconColStyle}><Icon name="check circle" style={{ color: '#21ba45', marginTop: '-6px', display: 'block' }} /></div>
          <div>
            <h3 style={{ marginBottom: '4px', fontSize: '18px' }}>Portfolio created</h3>
            <p style={{ marginBottom: '4px' }}>
              {result.portfolioName
                ? <><strong>{result.portfolioName}</strong> with {result.projectIds.length} project(s) has been created from the document.</>
                : <>Your portfolio with {result.projectIds.length} project has been created from the document.</>}
            </p>
            <p>
              <a
                href={`/portfolio/${result.portfolioId}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleViewPortfolio(result.portfolioId);
                }}
                style={{ color: 'var(--color-primary)', textDecoration: 'underline', fontWeight: 500 }}
              >
                View portfolio &rarr;
              </a>
            </p>
          </div>
        </div>
      );
    }
  }

  if (activeStatus === 'error' && progress?.result && progress.result.type === 'error') {
    const message = progress.result.message ?? 'An error occurred while creating your deal.';
    const details = progress.result.details;
    const step = progress.result.step;
    const request = progress.request;
    const stepLabels: Record<string, string> = {
      validating: 'Validation',
      downloading: 'File download',
      extracting: 'Text extraction',
      analyzing: 'AI analysis',
      creating: 'Deal creation',
    };
    return (
      <div className="card" style={rowStyle}>
        <div style={iconColStyle}><Icon name="exclamation circle" style={{ color: '#db2828', margin: 0, marginTop: '-6px', display: 'block' }} /></div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <h3 style={{ marginBottom: '4px', fontSize: '18px' }}>
            Creation failed{step && stepLabels[step] ? ` during ${stepLabels[step].toLowerCase()}` : ''}
          </h3>
          <p style={{ marginBottom: '4px' }}>{message}</p>
          {request?.fileName && (
            <p style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
              File: <strong>{request.fileName}</strong>
            </p>
          )}
          {request?.additionalContext && (
            <div style={{ marginBottom: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2px' }}>
                <span style={{ fontSize: '12px', color: '#666' }}>Your additional context:</span>
                <button
                  type="button"
                  onClick={() => handleCopyContext(request.additionalContext!)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    font: 'inherit',
                    fontSize: '12px',
                    color: 'var(--color-primary)',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                >
                  {copiedContext ? 'Copied' : 'Copy'}
                </button>
              </div>
              <pre style={{
                fontSize: '12px',
                color: '#333',
                background: '#f5f5f5',
                borderRadius: '4px',
                padding: '8px 10px',
                margin: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: '120px',
                overflow: 'auto',
              }}>
                {request.additionalContext}
              </pre>
            </div>
          )}
          {details && (
            <>
              <button
                type="button"
                onClick={() => setShowDetails((v) => !v)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  font: 'inherit',
                  fontSize: '12px',
                  color: '#888',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  marginBottom: '4px',
                }}
              >
                {showDetails ? 'Hide details' : 'Show details'}
              </button>
              {showDetails && (
                <pre style={{
                  fontSize: '11px',
                  color: '#666',
                  background: '#f5f5f5',
                  borderRadius: '4px',
                  padding: '8px 10px',
                  margin: '4px 0 8px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: '120px',
                  overflow: 'auto',
                }}>
                  {details}
                </pre>
              )}
            </>
          )}
          <p style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            {progress?.canRerun && (
              <button
                type="button"
                onClick={() => { void progress.rerun(); }}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  font: 'inherit',
                  color: 'var(--color-primary)',
                  textDecoration: 'underline',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Rerun
              </button>
            )}
            <button
              type="button"
              onClick={() => progress?.dismiss()}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                font: 'inherit',
                color: 'var(--color-primary)',
                textDecoration: 'underline',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Dismiss
            </button>
          </p>
        </div>
      </div>
    );
  }

  return null;
}
